import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import { query, where, getDocs } from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { toDate } from "../../utils/reports/format";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

/**
 * Completed Work Orders by Employee — counts completed work orders per person
 * over a date range (uses the new completedAt/completedBy fields, falling back
 * to legacy markedCompletedTime/markedCompleteBy).
 */
export default function GenerateCompletedWorkOrdersByEmployee({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const ref = propertyCollection(selectedClient, selectedProperty, "workOrders");
      const snap = await getDocs(query(ref, where("completed", "==", true)));

      const counts = new Map(); // employee -> count
      let total = 0;
      snap.docs.forEach((d) => {
        const wo = d.data();
        const when = toDate(wo.completedAt || wo.markedCompletedTime);
        if (!when || when < start || when > end) return;
        const who = wo.completedBy || wo.markedCompleteBy || "Unknown";
        counts.set(who, (counts.get(who) || 0) + 1);
        total += 1;
      });

      if (total === 0) {
        alert("No completed work orders found in that date range.");
        return;
      }

      const rows = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Completed Work Orders by Employee",
        dateRange: { start, end },
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const body = rows.map(([who, count]) => [who, String(count)]);
      body.push([
        { content: "TOTAL", styles: { fontStyle: "bold" } },
        { content: String(total), styles: { fontStyle: "bold", halign: "right" } },
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Employee", "Completed"]],
        body,
        styles: { font: "helvetica", fontSize: 10, cellPadding: 3 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        columnStyles: { 1: { halign: "right", cellWidth: 40 } },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Completed_WOs_By_Employee", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (e) {
      console.error("Error generating Completed WOs by Employee:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        Completed WOs by Employee
      </Button>
      <ReportDateRangeDialog
        open={open}
        title="Completed Work Orders by Employee"
        description="Counts work orders completed per employee in the selected date range."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
